import { z } from 'zod';

/** Pinterest's text limits (plan §3); the backend refuses longer fields per provider. */
export const PINTEREST_LIMITS = {
  title: 100,
  description: 800,
  altText: 500,
  link: 2048,
} as const;

/** One image, or two to five for a carousel. */
export const MAX_POST_MEDIA = 5;
export const MAX_POST_PRODUCTS = 20;

const HTTP_URL = /^https?:\/\/\S+$/i;

export const postTargetSchema = z.object({
  boardId: z.string().min(1),
  boardName: z.string().min(1).max(180),
  boardSectionId: z.string().nullable(),
});
export type PostTargetForm = z.infer<typeof postTargetSchema>;

/** A chosen variant with the product it belongs to; the backend refuses a variant whose product is not listed. */
export const postVariantSchema = z.object({
  variantId: z.string().min(1),
  productId: z.string().min(1),
  /** Shown on the chip; null for a variant loaded from a saved post. */
  title: z.string().nullable(),
});
export type PostVariantForm = z.infer<typeof postVariantSchema>;

/**
 * The editor's form. A draft may be saved with nothing but a product; the board and the media
 * are only required to publish, which the backend checks (409 SOCIAL_POST_NOT_PUBLISHABLE) and
 * the editor mirrors with `publishBlockers`.
 */
export const postFormSchema = z.object({
  productIds: z.array(z.string()).max(MAX_POST_PRODUCTS, 'social:validation.tooManyProducts'),
  variants: z.array(postVariantSchema).max(MAX_POST_PRODUCTS, 'social:validation.tooManyProducts'),
  mediaIds: z.array(z.string()).max(MAX_POST_MEDIA, 'social:validation.tooManyMedia'),
  target: postTargetSchema.nullable(),
  title: z.string().trim().max(PINTEREST_LIMITS.title, 'social:validation.titleLength'),
  description: z
    .string()
    .trim()
    .max(PINTEREST_LIMITS.description, 'social:validation.descriptionLength'),
  altText: z.string().trim().max(PINTEREST_LIMITS.altText, 'social:validation.altTextLength'),
  link: z
    .string()
    .trim()
    .max(PINTEREST_LIMITS.link, 'social:validation.linkLength')
    .refine((value) => value === '' || HTTP_URL.test(value), 'social:validation.link'),
});

export type PostFormInput = z.input<typeof postFormSchema>;
export type PostFormOutput = z.output<typeof postFormSchema>;

/** The schedule dialog: one wall-clock time on the store's clock, in the future. */
export const scheduleFormSchema = z.object({
  publishAt: z.string().min(1, 'social:validation.publishAt'),
});
export type ScheduleForm = z.infer<typeof scheduleFormSchema>;
